import { ReactNode, createContext, useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'

import { useLoadScript } from 'hooks/use-load-script'
import { debugLog } from 'utils/common'

const AnalyticsContext = createContext({
  pageName: '',
  setPageName: (name: string) => {},
  instance: undefined,
  sendPageLog: (_pageId: string, _customDimension?: object) => {},
  sendEventLog: (_pageId: string, _eventId: string, _customDimension?: object) => {}
})

type AnalyticsProviderProps = {
  children: ReactNode
}
const AnalyticsProvider = ({ children }: AnalyticsProviderProps): JSX.Element => {
  const [isScriptLoaded, setIsScriptLoaded] = useState<boolean>(false)
  const [instance, setInstance] = useState<any>(undefined)
  const [pageName, setPageName] = useState<string>('')

  const analyticsEnabled = process.env.REACT_APP_ANALYTICS_ENABLED === 'true'
  const analyticsDomain = process.env.REACT_APP_ANALYTICS_DOMAIN
  const trackingId = process.env.REACT_APP_ANALYTICS_TRACKING_ID
  const uiVersion = process.env.REACT_APP_ANALYTICS_UI_VERSION
  const errorMonitoringEnabled = process.env.REACT_APP_ANALYTICS_BACKGROUND_MONITORING_ENABLED === 'true'
  const performanceMonitoringEnabled = process.env.REACT_APP_ANALYTICS_BACKGROUND_MONITORING_ENABLED === 'true'
  const actionMonitoringEnabled = process.env.REACT_APP_ANALYTICS_ACTION_MONITORING_ENABLED === 'true'

  const [cookies] = useCookies()

  const adobeAnalyticsCookiesCustomDimensions = ['s_ecid', 's_cc', 's_sq', 's_vi', 's_fid', 's_ac'].reduce((acc, cookie) => {
    if (cookies[cookie]) {
      acc[cookie] = cookies[cookie]
    }
    return acc
  }, {})

  useLoadScript({
    enabled: analyticsEnabled,
    id: 'samsung-analytics-script',
    src: `${process.env.REACT_APP_ANALYTICS_LIBRARY_URL}`,
    onLoad: () => {
      if (!window.samsung?.analytics) {
        console.error('Failed to load samsung analytics script')
      } else {
        setIsScriptLoaded(true)
      }
    }
  })

  useEffect(() => {
    if (analyticsEnabled && isScriptLoaded) {
      window.samsung.analytics
        .init(analyticsDomain, trackingId, uiVersion, {
          deviceIdCookieName: 'sa_did',
          enableErrorMonitoring: errorMonitoringEnabled,
          enablePerformanceMonitoring: performanceMonitoringEnabled,
          enableActionMonitoring: actionMonitoringEnabled
        })
        .then(() => {
          setInstance(window.samsung.analytics)
        })
        .catch((error) => {
          console.error('Failed to analytics initialize', error)
        })
    }
  }, [analyticsEnabled, isScriptLoaded])

  const shouldNotSendAnalytics = () => {
    if (!analyticsEnabled) {
      debugLog('Analytics disabled')
      return true
    } else if (!instance) {
      debugLog('Analytics not initialized')
      return true
    }

    return false
  }

  const sendPageLog = (pageId: string, customDimension?: object) => {
    if (shouldNotSendAnalytics()) {
      return
    }

    try {
      instance.send(pageId, undefined, {
        sa_trace: cookies.sa_trace,
        ...adobeAnalyticsCookiesCustomDimensions,
        ...customDimension
      })
    } catch (err) {
      console.error('Failed to send page log', err)
    }
  }

  const sendEventLog = (pageId: string, eventId: string, customDimension?: object) => {
    if (shouldNotSendAnalytics()) {
      return
    }

    try {
      instance.send(pageId, eventId, {
        sa_trace: cookies.sa_trace,
        ...adobeAnalyticsCookiesCustomDimensions,
        ...customDimension
      })
    } catch (err) {
      console.error('Failed to send event log', err)
    }
  }

  return (
    <AnalyticsContext.Provider
      value={{
        pageName,
        setPageName,
        instance,
        sendPageLog,
        sendEventLog
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  )
}

export { AnalyticsContext, AnalyticsProvider }
